import * as THREE from 'three';
import type { ColorId, GameState, Pos } from '@color-dots/game';
import type { VisualStyle } from '../game/visualStyles.js';
import { projectOntoSphere } from './arcCurve3d.js';
import {
  getColorHex,
  getColorLight,
  getGearRimAccent,
  getGearRimColor,
} from './colors3d.js';
import {
  gearEndpointEmissiveIntensity,
  gearMetalMaterialEmissive,
} from './gearEmissivePolicy.js';
import {
  ENDPOINT_SPHERE_RADIUS,
  createEndpointRimGeometry,
} from './gearGeometry3d.js';
import { gridToWorld } from './Grid3DLayout.js';

export class DotsMeshes {
  readonly group = new THREE.Group();
  private dotMeshes = new Map<string, THREE.Object3D>();
  private visualStyle: VisualStyle = 'pipe';
  private sphereCenter = new THREE.Vector3(0, 0, 0);
  private sphereRadius = 0;

  setVisualStyle(style: VisualStyle): void {
    this.visualStyle = style;
  }

  /** Sphere center/radius (board-local space) used to seat endpoint dots on the glass sphere's surface. */
  setSphereBounds(center: THREE.Vector3, radius: number): void {
    this.sphereCenter.copy(center);
    this.sphereRadius = radius;
  }

  getVisualStyle(): VisualStyle {
    return this.visualStyle;
  }

  sync(state: GameState, activeColor: ColorId | null): void {
    const needed = new Set<string>();

    for (const [color, [a, b]] of state.endpoints) {
      this.upsertDot(a, color, color === activeColor, needed);
      this.upsertDot(b, color, color === activeColor, needed);
    }

    for (const [key, dot] of this.dotMeshes) {
      if (!needed.has(key)) {
        this.disposeDot(dot);
        this.group.remove(dot);
        this.dotMeshes.delete(key);
      }
    }
  }

  private upsertDot(
    pos: Pos,
    color: ColorId,
    isActive: boolean,
    needed: Set<string>,
  ): void {
    const key = `${pos[0]},${pos[1]}`;
    needed.add(key);

    const baseRadius = this.visualStyle === 'gear' ? ENDPOINT_SPHERE_RADIUS : 0.28;
    const lift = baseRadius * (isActive ? 0.6 : 0.4);
    const raw = gridToWorld(pos[0], pos[1], this.getSize());
    const world = this.sphereRadius > 1e-6
      ? projectOntoSphere(raw, this.sphereCenter, this.sphereRadius, lift)
      : raw;

    let dot = this.dotMeshes.get(key);
    if (!dot) {
      dot = this.visualStyle === 'gear'
        ? this.createGearEndpoint(color, isActive)
        : this.createPipeEndpoint(color, isActive);
      this.group.add(dot);
      this.dotMeshes.set(key, dot);
    } else {
      this.updateDot(dot, color, isActive);
    }

    dot.position.copy(world);
    if (this.sphereRadius > 1e-6) {
      const normal = world.clone().sub(this.sphereCenter).normalize();
      dot.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), normal);
    }
    dot.scale.setScalar(isActive ? 1.12 : 1);
  }

  private createPipeEndpoint(color: ColorId, isActive: boolean): THREE.Mesh {
    const radius = 0.28;
    const geo = new THREE.SphereGeometry(radius, 24, 24);
    const mat = new THREE.MeshStandardMaterial({
      color: getColorHex(color),
      emissive: getColorHex(color),
      emissiveIntensity: isActive ? 0.55 : 0.3,
      roughness: 0.35,
      metalness: 0.15,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.castShadow = true;
    mesh.name = 'pipe-dot';
    return mesh;
  }

  private createGearEndpoint(color: ColorId, isActive: boolean): THREE.Group {
    const group = new THREE.Group();
    group.name = 'gear-dot';

    const metalEmissive = gearMetalMaterialEmissive();
    const sphere = new THREE.Mesh(
      new THREE.SphereGeometry(ENDPOINT_SPHERE_RADIUS, 24, 24),
      new THREE.MeshStandardMaterial({
        color: getColorHex(color),
        emissive: getColorHex(color),
        emissiveIntensity: gearEndpointEmissiveIntensity(isActive),
        roughness: 0.3,
        metalness: 0.1,
      }),
    );
    sphere.name = 'sphere';
    sphere.castShadow = true;
    group.add(sphere);

    const rim = new THREE.Mesh(
      createEndpointRimGeometry(),
      new THREE.MeshStandardMaterial({
        color: getGearRimColor(color),
        roughness: 0.35,
        metalness: 0.75,
        ...metalEmissive,
      }),
    );
    rim.name = 'rim';
    rim.rotation.x = Math.PI / 2;
    rim.castShadow = true;
    group.add(rim);

    const accentHex = getGearRimAccent(color);
    if (accentHex) {
      const accent = new THREE.Mesh(
        new THREE.TorusGeometry(0.26, 0.018, 8, 20),
        new THREE.MeshStandardMaterial({
          color: accentHex,
          roughness: 0.4,
          metalness: 0.5,
          ...metalEmissive,
        }),
      );
      accent.name = 'rim-accent';
      accent.rotation.x = Math.PI / 2;
      group.add(accent);
    }

    if (isActive) {
      this.attachGlow(group, color, this.keyFromGroup(group));
    }

    return group;
  }

  private updateDot(dot: THREE.Object3D, color: ColorId, isActive: boolean): void {
    if (this.visualStyle === 'gear' && dot instanceof THREE.Group) {
      const sphere = dot.getObjectByName('sphere') as THREE.Mesh | undefined;
      if (sphere) {
        const mat = sphere.material as THREE.MeshStandardMaterial;
        mat.color.set(getColorHex(color));
        mat.emissive.set(getColorHex(color));
        mat.emissiveIntensity = gearEndpointEmissiveIntensity(isActive);
      }

      const rim = dot.getObjectByName('rim') as THREE.Mesh | undefined;
      if (rim) {
        (rim.material as THREE.MeshStandardMaterial).color.set(getGearRimColor(color));
      }

      const accent = dot.getObjectByName('rim-accent') as THREE.Mesh | undefined;
      const accentHex = getGearRimAccent(color);
      if (accentHex && accent) {
        (accent.material as THREE.MeshStandardMaterial).color.set(accentHex);
      }

      this.syncGlow(dot, color, isActive);
      return;
    }

    if (dot instanceof THREE.Mesh) {
      const mat = dot.material as THREE.MeshStandardMaterial;
      mat.color.set(getColorHex(color));
      mat.emissive.set(getColorHex(color));
      mat.emissiveIntensity = isActive ? 0.55 : 0.3;

      if (isActive) {
        this.attachGlow(dot, color, dot.uuid);
      } else {
        this.removeGlow(dot);
      }
    }
  }

  private syncGlow(dot: THREE.Group, color: ColorId, isActive: boolean): void {
    if (isActive) {
      this.attachGlow(dot, color, dot.uuid);
    } else {
      this.removeGlow(dot);
    }
  }

  private attachGlow(parent: THREE.Object3D, color: ColorId, id: string): void {
    const glowName = `glow-${id}`;
    let glow = parent.children.find((c) => c.name === glowName) as THREE.Mesh | undefined;
    const radius = this.visualStyle === 'gear' ? ENDPOINT_SPHERE_RADIUS : 0.28;
    if (!glow) {
      glow = new THREE.Mesh(
        new THREE.SphereGeometry(radius * 1.4, 16, 16),
        new THREE.MeshBasicMaterial({
          color: getColorLight(color),
          transparent: true,
          opacity: 0.25,
        }),
      );
      glow.name = glowName;
      parent.add(glow);
    } else {
      (glow.material as THREE.MeshBasicMaterial).color.set(getColorLight(color));
    }
  }

  private removeGlow(parent: THREE.Object3D): void {
    const glow = parent.children.find((c) => c.name.startsWith('glow-')) as THREE.Mesh | undefined;
    if (glow) {
      glow.geometry.dispose();
      (glow.material as THREE.Material).dispose();
      parent.remove(glow);
    }
  }

  private disposeDot(dot: THREE.Object3D): void {
    dot.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        if (child.geometry && child.geometry.type !== 'ExtrudeGeometry') {
          child.geometry.dispose();
        }
        if (Array.isArray(child.material)) {
          child.material.forEach((m) => m.dispose());
        } else {
          child.material.dispose();
        }
      }
    });
  }

  private keyFromGroup(group: THREE.Group): string {
    return group.uuid;
  }

  private size = 0;

  setSize(size: number): void {
    this.size = size;
  }

  private getSize(): number {
    return this.size || 5;
  }

  dispose(): void {
    for (const dot of this.dotMeshes.values()) {
      this.disposeDot(dot);
      this.group.remove(dot);
    }
    this.dotMeshes.clear();
  }
}
